import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cachedFetch,
  invalidateCache,
  peekCache,
  setCache,
} from '../lib/resourceCache';

/**
 * Cached resource loader for admin pages.
 * - Shows cached data immediately on remount (no full-page spinner)
 * - Revalidates in background when stale
 * - Dedupes parallel requests for the same key
 */
export function useCachedResource(key, fetcher, { staleTime = 60_000, enabled = true } = {}) {
  const initial = key ? peekCache(key) : null;
  const [data, setDataState] = useState(() => initial?.data ?? null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(() => enabled && Boolean(key) && initial?.data == null);
  const [refreshing, setRefreshing] = useState(false);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const dataRef = useRef(data);
  dataRef.current = data;
  const requestIdRef = useRef(0);

  const setData = useCallback(
    (updater) => {
      setDataState((current) => {
        const next = typeof updater === 'function' ? updater(current) : updater;
        if (key && next != null) {
          setCache(key, next);
        }
        return next;
      });
    },
    [key]
  );

  const reload = useCallback(
    async ({ force = false, silent = false } = {}) => {
      if (!enabled || !key) {
        return null;
      }

      const requestId = ++requestIdRef.current;
      const cached = peekCache(key);
      const hasData = cached?.data != null || dataRef.current != null;

      if (force) {
        invalidateCache(key);
      }

      if (!silent) {
        if (!hasData || force) {
          if (!hasData) setLoading(true);
          else setRefreshing(true);
        } else {
          setRefreshing(true);
        }
      } else if (hasData) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        // Serve fresh-enough cache instantly.
        if (!force && cached?.data != null && Date.now() - cached.updatedAt < staleTime) {
          if (requestId === requestIdRef.current) {
            setDataState(cached.data);
            setError(null);
            setLoading(false);
            setRefreshing(false);
          }
          return cached.data;
        }

        // Stale cache: paint immediately, then revalidate.
        if (!force && cached?.data != null) {
          if (requestId === requestIdRef.current) {
            setDataState(cached.data);
            setError(null);
            setLoading(false);
            setRefreshing(true);
          }
        }

        const result = await cachedFetch(key, () => fetcherRef.current(), {
          staleTime: force ? 0 : staleTime,
          force,
        });

        if (requestId !== requestIdRef.current) {
          return result.data;
        }

        setDataState(result.data);
        setError(null);
        return result.data;
      } catch (err) {
        if (requestId !== requestIdRef.current) {
          return null;
        }
        setError(err);
        if (dataRef.current == null && peekCache(key)?.data == null) {
          // keep empty
        }
        throw err;
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [enabled, key, staleTime]
  );

  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    if (!enabled || !key) {
      return undefined;
    }

    const cached = peekCache(key);
    if (cached?.data != null) {
      setDataState(cached.data);
      setLoading(false);
    }

    void reloadRef.current({ silent: cached?.data != null }).catch(() => {
      // error state handled in reload
    });

    return undefined;
  }, [key, enabled, staleTime]);

  return {
    data,
    setData,
    error,
    loading,
    refreshing,
    reload,
  };
}

export { invalidateCache } from '../lib/resourceCache';
